import React, { useState, useEffect, useRef } from 'react';
import { Prompt, useLocation, useHistory } from 'react-router-dom';
import { Grid, IconButton } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import ActionPreview from '../../common/CreateActionPreviews/ActionPreview';
import { getActiveCommunityId } from '../../user/selectors';
import { connect } from 'react-redux';
import { getToken } from '../../auth/selectors';
import {
    clearThemesErrors,
    themesRequested,
} from '../../communityThemes/actions';
import {
    areThemesLoading,
    getThemes,
    getThemesErrors,
} from '../../communityThemes/selectors';
import StyledCreateActionForm from './CreateActionForm';
import Loader from '../../common/OnBoarding/Loader';
import SnackBarAPIResponse from '../../common/SnackbarAlert/SnackBarAPIResponse';
import { cleanEmptyValues } from '../../utils/cleanEmptyValues';
import {
    clearActionErrors,
    createActionRequested,
    urlMetadataRequested,
    clearActionLinkMetadata,
} from '../../actions/actions';
import {
    getAction,
    getActionErrors,
    getMetadata,
    isMetadataLoading,
} from '../../actions/selectors';
import ConfirmModal from '../../common/ConfirmModal';
import { useTranslation } from 'react-i18next';
import {
    BUTTON_ACCENT_COLOR,
    BUTTON_DEFAULT_COLOR,
} from '../../common/Button/constants';
import { DEFAULT_TIME_LIMIT } from './constants';

const styles = (theme) => ({
    container: {
        padding: theme.spacing(2),
        [theme.breakpoints.up('md')]: {
            padding: theme.spacing(5),
        },
    },
    previewContainer: {
        [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(4),
        },
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
    },
    errorMessage: {
        color: theme.palette.error.main,
        textAlign: 'center',
    },
});

const CreateActionScreen = ({
    classes,
    communityId,
    fetchThemes,
    areThemesLoading,
    themesErrors,
    clearErrors,
    themes,
    createAction,
    action,
    actionErrors,
    metadata,
    isMetadataLoading,
    requestMetadataForUrl,
    clearMetadata,
}) => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const selectedThemeId = queryParams.get('themeId');
    const [actionData, setActionData] = useState({
        name: { en: '', es: '' },
        description: { en: '', es: '' },
        bonusPoints: 1,
        coverPhoto: null,
        icon: { name: '' },
        links: [],
        videoLinks: [],
        keywords: '',
        timeRequirements: [DEFAULT_TIME_LIMIT],
        categoryId: communityId,
        themeId: selectedThemeId,
        themeName: '',
        sponsors: [],
        impactConversions: [],
        sustainableDevelopmentGoals: [],
    });
    const [isDraft, setIsDraft] = useState(false);

    const [scrollTarget, setScrollTarget] = useState(null); // Add scroll target state

    const handleScrollTo = (target) => {
        setScrollTarget(target); // Update the scroll target
    };

    useEffect(() => {
        fetchThemes(communityId, true);
    }, [communityId, fetchThemes]);

    const [selectedTheme, setSelectedTheme] = useState(null);

    const themesList = themes || [];

    useEffect(() => {
        setSelectedTheme(themesList.find((t) => t.id === selectedThemeId));
    }, [themesList, selectedThemeId]);

    useEffect(() => {
        if (selectedTheme) {
            setActionData((prevData) => ({
                ...prevData,
                themeName: selectedTheme.name,
            }));
        }
    }, [selectedTheme]);

    useEffect(() => {
        if (action.id) {
            history.replace(`/actions?communityId=${communityId}`);
            history.push(`/actions/${action.id}?communityId=${communityId}`);
        }
    }, [action.id, communityId, history]);

    const getImageUrlFromSizes = (sizes) => {
        if (Array.isArray(sizes) && sizes.length > 0) {
            return sizes[0]?.file?.url;
        }
        return '';
    };

    // Prepare data for preview
    const previewData = {
        ...actionData,
        name:
            typeof actionData.name === 'object'
                ? actionData.name.en
                : actionData.name,
        description:
            typeof actionData.description === 'object'
                ? actionData.description.en
                : actionData.description,
        coverPhoto: getImageUrlFromSizes(actionData.coverPhoto?.photo?.sizes),
        photoAuthorName: actionData.coverPhoto?.photoAuthorName || '',
        icon: getImageUrlFromSizes(actionData.icon?.sizes),
        videoLinks: actionData.videoLinks || [],
        links: actionData.links || [],
        keywords: actionData.keywords || '',
    };

    const handleFormChange = (newData) => {
        if (!isDraft) {
            setIsDraft(true);
        }
        setActionData((prevData) => {
            const updatedData = { ...prevData };

            Object.keys(newData).forEach((key) => {
                if (key === 'coverPhoto') {
                    // Handle coverPhoto updates
                    updatedData[key] =
                        newData[key] === null
                            ? null
                            : {
                                  ...prevData[key],
                                  ...newData[key],
                                  photoAuthorName:
                                      newData[key]?.photoAuthorName ||
                                      prevData[key]?.photoAuthorName ||
                                      '',
                              };
                } else if (key === 'icon') {
                    // Handle icon updates
                    updatedData[key] =
                        newData[key] === null
                            ? null
                            : {
                                  ...prevData[key],
                                  ...newData[key],
                              };
                } else if (key === 'links' || key === 'videoLinks') {
                    // Handle links and videoLinks updates
                    if (Array.isArray(newData[key])) {
                        // Replace the entire array if newData[key] is an array
                        updatedData[key] = newData[key];
                    } else if (typeof newData[key] === 'string') {
                        // Append a single link to the existing array
                        updatedData[key] = [
                            ...(prevData[key] || []),
                            newData[key],
                        ];
                    }
                } else if (
                    typeof newData[key] === 'object' &&
                    newData[key] !== null
                ) {
                    updatedData[key] = { ...prevData[key], ...newData[key] };
                } else {
                    updatedData[key] = newData[key];
                }
            });

            return updatedData;
        });
    };

    const processedLinks = useRef(new Set());

    useEffect(() => {
        const isValidUrl = (url) =>
            Boolean(url.match(/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i));

        actionData.links.forEach((link) => {
            if (!processedLinks.current.has(link) && isValidUrl(link)) {
                requestMetadataForUrl({ url: link, saveImage: true });
                processedLinks.current.add(link);
            }
        });
    }, [actionData.links, requestMetadataForUrl]);

    useEffect(() => {
        return () => {
            clearMetadata();
        };
    }, [clearMetadata]);

    const handleSubmit = async (finalData) => {
        const fullActionData = { ...actionData, ...finalData };

        // Format links to ensure they have proper protocol
        const formatUrl = (url) => {
            if (!url) return url;
            return url.startsWith('http') ? url : `https://${url}`;
        };

        // Strip video links to just video IDs
        const videoLinks = (fullActionData.videoLinks || []).map(
            (v) => new URL(v).searchParams.get('v') || v,
        );
        // Format links with metadata
        const links = (fullActionData.links || []).map((link) => {
            const meta = metadata[link] || {};
            return {
                title: meta.title
                    ? { en: meta.title }
                    : { en: 'Default Title' },
                url: formatUrl(link),
                description: meta.description
                    ? { en: meta.description }
                    : { en: link },
                image: meta.image || null,
            };
        });

        const submissionData = {
            ...fullActionData,
            links,
            videoLinks,
        };

        const cleanedData = cleanEmptyValues(submissionData);

        createAction(cleanedData, communityId);
        setIsDraft(false);
    };

    const [leaveConfirmationShown, setLeaveConfirmationShown] = useState(false);
    const [leaveLocation, setLeaveLocation] = useState(null);
    const [leaveConfirmed, setLeaveConfirmed] = useState(false);

    useEffect(() => {
        if (leaveConfirmed) {
            history.push(leaveLocation);
        }
    }, [history, leaveConfirmed, leaveLocation]);

    return (
        <>
            <Prompt
                when={isDraft}
                message={(nextLocation) => {
                    if (!leaveConfirmed) {
                        setLeaveLocation(nextLocation);
                        setLeaveConfirmationShown(true);
                        return false;
                    }
                    return true;
                }}
            />
            {isDraft && (
                <ConfirmModal
                    title={t('unsavedTitle')}
                    confirmBtnText={t('leave')}
                    isOpen={leaveConfirmationShown}
                    setOpen={setLeaveConfirmationShown}
                    confirmBtnType={BUTTON_ACCENT_COLOR}
                    cancelBtnType={BUTTON_DEFAULT_COLOR}
                    onConfirm={() => {
                        setLeaveConfirmed(true);
                    }}
                    onClose={() => {
                        setLeaveConfirmationShown(false);
                    }}
                    onCancel={() => {
                        setLeaveConfirmationShown(false);
                    }}
                >
                    {t('unsavedWarning')}
                </ConfirmModal>
            )}
            <IconButton className={classes.backIcon} onClick={history.goBack}>
                <ArrowBack />
            </IconButton>
            <Grid container spacing={4} className={classes.container}>
                <Grid item xs={12} md={6}>
                    {areThemesLoading && <Loader />}
                    <SnackBarAPIResponse
                        errors={[
                            ...(themesErrors || []),
                            ...(actionErrors || []),
                        ]}
                        clearResponse={clearErrors}
                    />
                    <StyledCreateActionForm
                        initialData={actionData}
                        onSubmit={handleSubmit}
                        onChangeValue={handleFormChange}
                        selectedTheme={selectedTheme}
                        onScrollTo={handleScrollTo}
                    />
                </Grid>
                <Grid item xs={12} md={6} className={classes.previewContainer}>
                    <ActionPreview
                        {...previewData}
                        selectedTheme={selectedTheme}
                        scrollTarget={scrollTarget}
                        metadata={metadata}
                        isMetadataLoading={isMetadataLoading}
                    />
                </Grid>
            </Grid>
        </>
    );
};

const mapStateToProps = (state) => ({
    communityId: getActiveCommunityId(state),
    auth: getToken(state),
    themes: getThemes(state),
    areThemesLoading: areThemesLoading(state),
    themesErrors: getThemesErrors(state),
    action: getAction(state),
    actionErrors: getActionErrors(state),
    metadata: getMetadata(state),
    isMetadataLoading: isMetadataLoading(state),
});

const mapDispatchToProps = (dispatch) => ({
    fetchThemes: (communityId, includeCategories) =>
        dispatch(themesRequested(communityId, includeCategories)),
    createAction: (actionData, groupId) =>
        dispatch(createActionRequested(actionData, groupId)),
    clearErrors: () => {
        dispatch(clearActionErrors());
        dispatch(clearThemesErrors());
    },
    requestMetadataForUrl: (request) => dispatch(urlMetadataRequested(request)),
    clearMetadata: () => dispatch(clearActionLinkMetadata()),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles)(CreateActionScreen));
